import React, { useState } from 'react';
import { RichTextEditor } from '../editor/RichTextEditor';
import { useResizable } from '../../hooks/useResizable';

type TabType = 'notes' | 'tags';

export function JournalSidebar() {
    const [activeTab, setActiveTab] = useState<TabType>('notes');
    const [tags, setTags] = useState<string[]>(['Trading Psychology', 'Market Analysis', 'Risk Management']);
    const [newTag, setNewTag] = useState('');
    const { width, isResizing, startResize } = useResizable({
        initialWidth: 320,
        minWidth: 280,
        maxWidth: 600,
        storageKey: 'journal-sidebar-width'
    });

    const handleAddTag = () => {
        if (newTag.trim() && !tags.includes(newTag.trim())) {
            setTags([...tags, newTag.trim()]);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleAddTag();
        }
    };

    return (
        <div 
            className="border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex flex-col h-full relative"
            style={{ width: `${width}px` }}
        >
            {/* Resize Handle */}
            <div
                className={`absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize group hover:bg-blue-600/10 transition-colors ${
                    isResizing ? 'bg-blue-600/20' : 'bg-transparent'
                }`}
                onMouseDown={startResize}
                style={{ transform: 'translateX(-6px)' }}
                title="Drag to resize sidebar"
            >
                {/* Prominent visible drag indicator */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-16 bg-blue-600 rounded-full opacity-60 group-hover:opacity-100 transition-opacity" />
                {/* Three dots indicator for better visibility */}
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1">
                    <div className="w-1 h-1 bg-blue-600 rounded-full opacity-80 group-hover:opacity-100" />
                    <div className="w-1 h-1 bg-blue-600 rounded-full opacity-80 group-hover:opacity-100" />
                    <div className="w-1 h-1 bg-blue-600 rounded-full opacity-80 group-hover:opacity-100" />
                </div>
            </div>
            {/* Tab Headers */}
            <div className="border-b border-zinc-200 dark:border-zinc-800 p-4">
                <div className="flex space-x-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-1">
                    <button
                        onClick={() => setActiveTab('notes')}
                        className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            activeTab === 'notes'
                                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm'
                                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                        }`}
                    >
                        Notes
                    </button>
                    <button
                        onClick={() => setActiveTab('tags')}
                        className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            activeTab === 'tags'
                                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm'
                                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                        }`}
                    >
                        Tags
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'notes' && (
                    <div className="p-4 h-full">
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                                General Notes
                            </label>
                        </div>
                        <div className="h-64">
                            <RichTextEditor />
                        </div>
                        
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                                Quick Notes
                            </label>
                            <textarea
                                className="w-full h-32 px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg resize-none text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Add quick notes here..."
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'tags' && (
                    <div className="p-4 h-full flex flex-col">
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                                Add New Tag
                            </label>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    className="flex-1 px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter tag name..."
                                />
                                <button
                                    onClick={handleAddTag}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                                >
                                    Add
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                                Current Tags
                            </label>
                            <div className="space-y-2">
                                {tags.map((tag, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-2 bg-zinc-50 dark:bg-zinc-800 rounded-lg group"
                                    >
                                        <span className="text-sm text-zinc-700 dark:text-zinc-300 flex-1 truncate">
                                            {tag}
                                        </span>
                                        <button
                                            onClick={() => handleRemoveTag(tag)}
                                            className="ml-2 text-zinc-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Remove tag"
                                        >
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                {tags.length === 0 && (
                                    <div className="text-center py-8 text-zinc-500 dark:text-zinc-400 text-sm">
                                        No tags yet. Add your first tag above.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}